import { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../ui/button';
import { AlertTriangle, Shield, User, MessageSquare, Ban, Eye, CheckCircle, XCircle, Clock, ChevronDown, ChevronUp } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminReportsPage() {
  const { user } = useAuth();
  const [reports, setReports] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('pending');
  const [expandedReport, setExpandedReport] = useState(null);
  const [actionLoading, setActionLoading] = useState(null);

  useEffect(() => {
    if (user?.role === 'admin') {
      fetchReports();
      fetchStats();
    }
  }, [user, filter]);

  const fetchReports = async () => {
    try {
      const params = filter !== 'all' ? `?status=${filter}` : '';
      const response = await fetch(`${API_URL}/api/admin/reports${params}`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setReports(data.reports);
      } else {
        setError('Failed to fetch reports');
      }
    } catch (err) {
      setError('Error loading reports');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/stats/penpal`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setStats(data);
      }
    } catch (err) {
      console.error('Failed to fetch stats:', err);
    }
  };

  const updateReportStatus = async (reportId, status, action = null, notes = null) => {
    setActionLoading(reportId);
    try {
      const response = await fetch(`${API_URL}/api/admin/reports/${reportId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          status,
          action_taken: action,
          admin_notes: notes
        })
      });
      if (response.ok) {
        fetchReports();
        fetchStats();
      }
    } catch (err) {
      console.error('Failed to update report:', err);
    } finally {
      setActionLoading(null);
    }
  };

  const handleUserAction = async (profileId, action) => {
    setActionLoading(profileId);
    try {
      let endpoint = '';
      let method = 'POST';
      let body = {};

      switch (action) {
        case 'warn':
          endpoint = `/api/admin/users/${profileId}/warn`;
          break;
        case 'suspend':
          endpoint = `/api/admin/users/${profileId}/suspend`;
          break;
        case 'unsuspend':
          endpoint = `/api/admin/users/${profileId}/unsuspend`;
          break;
        case 'ban':
          const reason = window.prompt('Enter ban reason:');
          if (!reason) {
            setActionLoading(null);
            return;
          }
          endpoint = `/api/admin/users/${profileId}/ban`;
          body = { reason };
          break;
        case 'unban':
          endpoint = `/api/admin/users/${profileId}/unban`;
          break;
        default:
          return;
      }

      const response = await fetch(`${API_URL}${endpoint}`, {
        method,
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: Object.keys(body).length > 0 ? JSON.stringify(body) : undefined
      });

      if (response.ok) {
        fetchReports();
        alert(`Action "${action}" completed successfully`);
      } else {
        const data = await response.json();
        alert(`Failed: ${data.detail || 'Unknown error'}`);
      }
    } catch (err) {
      console.error('Action failed:', err);
      alert('Action failed');
    } finally {
      setActionLoading(null);
    }
  };

  if (!user || user.role !== 'admin') {
    return (
      <div className="max-w-4xl mx-auto px-6 py-16 text-center">
        <Shield className="w-16 h-16 text-red-400 mx-auto mb-4" />
        <h1 className="text-2xl font-serif text-stone-800 mb-2">Admin Access Required</h1>
        <p className="text-stone-600">You must be an administrator to view this page.</p>
      </div>
    );
  }

  const statusColors = {
    pending: 'bg-amber-100 text-amber-800',
    reviewed: 'bg-blue-100 text-blue-800',
    action_taken: 'bg-green-100 text-green-800',
    dismissed: 'bg-stone-100 text-stone-600'
  };

  const statusIcons = {
    pending: <Clock className="w-4 h-4" />,
    reviewed: <Eye className="w-4 h-4" />,
    action_taken: <CheckCircle className="w-4 h-4" />,
    dismissed: <XCircle className="w-4 h-4" />
  };

  return (
    <div className="max-w-7xl mx-auto px-6 py-12" data-testid="admin-reports-page">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-serif text-stone-800 mb-2">Pen Pal Reports Dashboard</h1>
        <p className="text-stone-600">Review and manage user reports</p>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <div className="bg-white border border-stone-200 rounded-lg p-4">
            <div className="text-2xl font-serif text-sage-700">{stats.profiles.active}</div>
            <div className="text-sm text-stone-500">Active Profiles</div>
          </div>
          <div className="bg-white border border-stone-200 rounded-lg p-4">
            <div className="text-2xl font-serif text-sage-700">{stats.connections.matched}</div>
            <div className="text-sm text-stone-500">Matched Connections</div>
          </div>
          <div className="bg-white border border-stone-200 rounded-lg p-4">
            <div className="text-2xl font-serif text-amber-600">{stats.reports.pending}</div>
            <div className="text-sm text-stone-500">Pending Reports</div>
          </div>
          <div className="bg-white border border-stone-200 rounded-lg p-4">
            <div className="text-2xl font-serif text-stone-700">{stats.messages.total}</div>
            <div className="text-sm text-stone-500">Total Messages</div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap gap-2 mb-6">
        {['all', 'pending', 'reviewed', 'action_taken', 'dismissed'].map((status) => (
          <button
            key={status}
            onClick={() => setFilter(status)}
            className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
              filter === status
                ? 'bg-sage-600 text-white'
                : 'bg-stone-100 text-stone-600 hover:bg-stone-200'
            }`}
          >
            {status === 'all' ? 'All Reports' : status.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
          </button>
        ))}
      </div>

      {/* Reports List */}
      {loading ? (
        <div className="text-center py-12">
          <div className="animate-spin w-8 h-8 border-2 border-sage-600 border-t-transparent rounded-full mx-auto mb-4"></div>
          <p className="text-stone-500">Loading reports...</p>
        </div>
      ) : error ? (
        <div className="text-center py-12 text-red-600">{error}</div>
      ) : reports.length === 0 ? (
        <div className="text-center py-12 bg-stone-50 rounded-lg">
          <CheckCircle className="w-12 h-12 text-sage-500 mx-auto mb-4" />
          <p className="text-stone-600">No {filter !== 'all' ? filter.replace('_', ' ') : ''} reports found</p>
        </div>
      ) : (
        <div className="space-y-4">
          {reports.map((report) => (
            <div
              key={report.id}
              className="bg-white border border-stone-200 rounded-lg overflow-hidden"
              data-testid={`report-${report.id}`}
            >
              {/* Report Header */}
              <div
                className="p-4 cursor-pointer hover:bg-stone-50 transition-colors"
                onClick={() => setExpandedReport(expandedReport === report.id ? null : report.id)}
              >
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-4">
                    <div className="p-2 bg-red-50 rounded-lg">
                      <AlertTriangle className="w-5 h-5 text-red-500" />
                    </div>
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-medium text-stone-800">
                          {report.reported.display_name}
                        </span>
                        <span className="text-stone-400">reported by</span>
                        <span className="text-stone-600">{report.reporter.display_name}</span>
                        {report.previous_reports_count > 0 && (
                          <span className="px-2 py-0.5 bg-red-100 text-red-700 text-xs rounded-full">
                            {report.previous_reports_count} previous reports
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-stone-500 line-clamp-2">{report.reason}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className={`px-3 py-1 rounded-full text-xs font-medium flex items-center gap-1 ${statusColors[report.status]}`}>
                      {statusIcons[report.status]}
                      {report.status.replace('_', ' ')}
                    </span>
                    {expandedReport === report.id ? (
                      <ChevronUp className="w-5 h-5 text-stone-400" />
                    ) : (
                      <ChevronDown className="w-5 h-5 text-stone-400" />
                    )}
                  </div>
                </div>
              </div>

              {/* Expanded Details */}
              {expandedReport === report.id && (
                <div className="border-t border-stone-200 p-4 bg-stone-50">
                  <div className="grid md:grid-cols-2 gap-6 mb-6">
                    {/* Reporter Info */}
                    <div>
                      <h4 className="text-sm font-medium text-stone-500 mb-2 flex items-center gap-1">
                        <User className="w-4 h-4" /> Reporter
                      </h4>
                      <div className="bg-white p-3 rounded-lg border border-stone-200">
                        <p className="font-medium">{report.reporter.display_name}</p>
                        <p className="text-sm text-stone-500">{report.reporter.email}</p>
                        <p className="text-sm text-stone-400">{report.reporter.country}</p>
                      </div>
                    </div>

                    {/* Reported User Info */}
                    <div>
                      <h4 className="text-sm font-medium text-stone-500 mb-2 flex items-center gap-1">
                        <AlertTriangle className="w-4 h-4" /> Reported User
                      </h4>
                      <div className="bg-white p-3 rounded-lg border border-stone-200">
                        <div className="flex items-center gap-2">
                          <p className="font-medium">{report.reported.display_name}</p>
                          {report.reported.is_banned && (
                            <span className="px-2 py-0.5 bg-red-100 text-red-700 text-xs rounded">BANNED</span>
                          )}
                          {!report.reported.is_active && !report.reported.is_banned && (
                            <span className="px-2 py-0.5 bg-amber-100 text-amber-700 text-xs rounded">SUSPENDED</span>
                          )}
                        </div>
                        <p className="text-sm text-stone-500">{report.reported.email}</p>
                        <p className="text-sm text-stone-400">{report.reported.country}</p>
                      </div>
                    </div>
                  </div>

                  {/* Full Reason */}
                  <div className="mb-6">
                    <h4 className="text-sm font-medium text-stone-500 mb-2 flex items-center gap-1">
                      <MessageSquare className="w-4 h-4" /> Report Reason
                    </h4>
                    <div className="bg-white p-3 rounded-lg border border-stone-200">
                      <p className="text-stone-700 whitespace-pre-wrap">{report.reason}</p>
                    </div>
                  </div>

                  {/* Admin Notes (if any) */}
                  {report.admin_notes && (
                    <div className="mb-6">
                      <h4 className="text-sm font-medium text-stone-500 mb-2">Admin Notes</h4>
                      <div className="bg-blue-50 p-3 rounded-lg border border-blue-200">
                        <p className="text-stone-700">{report.admin_notes}</p>
                        {report.reviewed_by && (
                          <p className="text-xs text-stone-500 mt-2">
                            Reviewed by {report.reviewed_by} on {new Date(report.reviewed_at).toLocaleDateString()}
                          </p>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Actions */}
                  <div className="flex flex-wrap gap-3">
                    {/* Report Status Actions */}
                    {report.status === 'pending' && (
                      <>
                        <Button
                          onClick={() => updateReportStatus(report.id, 'reviewed')}
                          disabled={actionLoading === report.id}
                          variant="outline"
                          className="text-blue-600 border-blue-200 hover:bg-blue-50"
                        >
                          <Eye className="w-4 h-4 mr-1" /> Mark Reviewed
                        </Button>
                        <Button
                          onClick={() => updateReportStatus(report.id, 'dismissed', 'none', 'No action required')}
                          disabled={actionLoading === report.id}
                          variant="outline"
                          className="text-stone-600"
                        >
                          <XCircle className="w-4 h-4 mr-1" /> Dismiss
                        </Button>
                      </>
                    )}

                    {/* User Actions */}
                    {!report.reported.is_banned && (
                      <>
                        <Button
                          onClick={() => {
                            handleUserAction(report.reported.profile_id, 'warn');
                            updateReportStatus(report.id, 'action_taken', 'warning');
                          }}
                          disabled={actionLoading}
                          variant="outline"
                          className="text-amber-600 border-amber-200 hover:bg-amber-50"
                        >
                          <AlertTriangle className="w-4 h-4 mr-1" /> Warn User
                        </Button>

                        {report.reported.is_active ? (
                          <Button
                            onClick={() => {
                              handleUserAction(report.reported.profile_id, 'suspend');
                              updateReportStatus(report.id, 'action_taken', 'suspension');
                            }}
                            disabled={actionLoading}
                            variant="outline"
                            className="text-orange-600 border-orange-200 hover:bg-orange-50"
                          >
                            <Shield className="w-4 h-4 mr-1" /> Suspend
                          </Button>
                        ) : (
                          <Button
                            onClick={() => handleUserAction(report.reported.profile_id, 'unsuspend')}
                            disabled={actionLoading}
                            variant="outline"
                            className="text-green-600 border-green-200 hover:bg-green-50"
                          >
                            <CheckCircle className="w-4 h-4 mr-1" /> Unsuspend
                          </Button>
                        )}

                        <Button
                          onClick={() => {
                            handleUserAction(report.reported.profile_id, 'ban');
                            updateReportStatus(report.id, 'action_taken', 'ban');
                          }}
                          disabled={actionLoading}
                          variant="outline"
                          className="text-red-600 border-red-200 hover:bg-red-50"
                        >
                          <Ban className="w-4 h-4 mr-1" /> Ban User
                        </Button>
                      </>
                    )}

                    {report.reported.is_banned && (
                      <Button
                        onClick={() => handleUserAction(report.reported.profile_id, 'unban')}
                        disabled={actionLoading}
                        variant="outline"
                        className="text-green-600 border-green-200 hover:bg-green-50"
                      >
                        <CheckCircle className="w-4 h-4 mr-1" /> Unban User
                      </Button>
                    )}
                  </div>

                  {/* Timestamp */}
                  <div className="mt-4 pt-4 border-t border-stone-200 text-xs text-stone-400">
                    Report submitted: {new Date(report.created_at).toLocaleString()}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
